/**
 * useDismissGettingStarted — PATCH /api/t/{slug}/getting-started {dismissed:true}.
 *
 * Retires the first-run onboarding experience (welcome + checklist). Optimistic:
 * flips `first_run` to false on the cached state so the UX unmounts immediately;
 * reverts on error. Invalidates on settle so the server-computed gate is the
 * source of truth on the next read.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import {
  gettingStartedKey,
  type GettingStartedState,
} from "@/hooks/queries/auth/useGettingStarted";

export function useDismissGettingStarted() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const key = gettingStartedKey(tenantSlug);

  return useMutation<
    void,
    ApiError,
    void,
    { previous: GettingStartedState | undefined }
  >({
    mutationFn: async () => {
      await apiClient.patch(`/api/t/${tenantSlug}/getting-started`, {
        dismissed: true,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GettingStartedState>(key);
      if (previous) {
        queryClient.setQueryData<GettingStartedState>(key, {
          ...previous,
          dismissed_at: new Date().toISOString(),
          first_run: false,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData<GettingStartedState>(key, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
